<template>
  <div class="admin-panel task-page task-page--billing">

    <admin-header
      variant="back"
      :crumbs="[{ label: 'Client Management', to: { name: 'Admin-Panel', params: { lang: $route.params.lang } } }, { label: 'Billing Tasks' }]"
      :back-to="{ name: 'Admin-Panel', params: { lang: $route.params.lang } }"
      @open-logout="openLogoutModal"
    />

    <confirm-modal
      :show="showLogoutModal"
      title="Confirm Logout"
      confirm-label="Logout"
      :icon="['fas', 'sign-out-alt']"
      @cancel="closeLogoutModal"
      @confirm="confirmLogout"
    >Are you sure you want to logout?</confirm-modal>

    <session-modal :show="showAdminSessionModal" @continue="continueAdminSession" @signout="signOutAdminSession" />
    <alert-modal :show="showPopup" :message="popupMessage" :status="popupStatus" @close="closePopup" />
    <client-details-modal
      :show="showViewModal"
      :client="viewingClient"
      :show-documents="false"
      @close="showViewModal = false"
    />

    <main class="ap-container ap-fade-in">

      <!-- Hero -->
      <div class="tp-hero">
        <div class="tp-hero-icon">
          <fa-icon :icon="['fas', 'tags']" />
        </div>
        <div>
          <h1 class="tp-hero-title">Billing</h1>
          <p class="tp-hero-sub">Review and manage client billing fees, invoice dates, and commissions.</p>
        </div>
      </div>

      <!-- Billing panel -->
      <div class="tp-panel">
        <div class="tp-panel-head">
          <h3>Billing Table</h3>
          <div class="tp-panel-head-right">
            <div class="tp-year-tabs">
              <button
                v-for="s in statusOptions"
                :key="s.value"
                class="tp-year-tab"
                :class="{ 'is-active': statusFilter === s.value }"
                @click="statusFilter = s.value; currentPage = 1"
              >{{ s.label }}</button>
            </div>
            <div class="ap-search bt-search">
              <fa-icon :icon="['fas', 'search']" class="ap-search-icon" />
              <input
                v-model="searchQuery"
                type="search"
                placeholder="Search by client…"
                @input="currentPage = 1"
              />
            </div>
            <button
              class="tp-notify-btn"
              :disabled="saving || loading"
              @click="saveAll"
            >
              <fa-icon :icon="['fas', saving ? 'spinner' : 'save']" :spin="saving" />
              {{ saving ? 'Saving…' : 'Update All' }}
            </button>
          </div>
        </div>

        <!-- Loading -->
        <div v-if="loading" class="tp-empty">
          <fa-icon :icon="['fas', 'spinner']" class="tp-empty-icon" spin />
          <p>Loading billing data…</p>
        </div>

        <!-- Empty -->
        <div v-else-if="!filteredRows.length" class="tp-empty">
          <fa-icon :icon="['fas', 'tags']" class="tp-empty-icon" />
          <p>{{ searchQuery ? 'No clients match your search.' : 'No ' + statusFilter + ' cosec clients found.' }}</p>
        </div>

        <!-- Table -->
        <div ref="calWrap" class="bt-table-wrap" :style="{ height: tableHeight }">
          <table class="bt-table">
            <thead>
              <tr>
                <th class="bt-th bt-th-num bt-sticky bt-sticky-0">#</th>
                <th class="bt-th bt-th-client bt-sticky bt-sticky-num">Client's Full Name</th>
                <th class="bt-th">Initial</th>
                <th class="bt-th">Incorporation Date</th>
                <th class="bt-th">Inception Date<br><small>(Cosec)</small></th>
                <th class="bt-th bt-th-amount">Monthly Fee<br><small>(Cosec)</small></th>
                <th class="bt-th bt-th-amount">Monthly Fee<br><small>(SST)</small></th>
                <th class="bt-th bt-th-amount">Monthly Fee<br><small>(Payroll)</small></th>
                <th class="bt-th bt-th-date">Next Billing Date<br><small>(Cosec)</small></th>
                <th class="bt-th bt-th-date">Latest Invoice Date<br><small>(Cosec)</small></th>
                <th class="bt-th bt-th-amount">Tax</th>
                <th class="bt-th bt-th-amount">Cosec</th>
                <th class="bt-th bt-th-amount">SST</th>
                <th class="bt-th bt-th-amount">Payroll Monthly</th>
                <th class="bt-th bt-th-amount">Payroll Yearly</th>
                <th class="bt-th bt-th-amount">TP Reminder</th>
                <th class="bt-th bt-th-total">Total</th>
                <th class="bt-th bt-th-text">Commission To</th>
                <th class="bt-th bt-th-text">Commission From</th>
                <th class="bt-th"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, i) in paginatedRows" :key="row.id" class="bt-row">
                <td class="bt-td bt-td-num bt-sticky bt-sticky-0">{{ pageOffset + i + 1 }}</td>
                <td class="bt-td bt-td-client bt-sticky bt-sticky-num">{{ row.client || '—' }}</td>
                <td class="bt-td bt-td-meta">{{ row.initial || '—' }}</td>
                <td class="bt-td bt-td-meta" :class="{ 'ap-cell-empty': !row.incorporation_date }">{{ formatIncDate(row.incorporation_date) }}</td>
                <td class="bt-td bt-td-meta" :class="{ 'ap-cell-empty': !row.inception_date }">{{ formatIncDate(row.inception_date) }}</td>
                <td class="bt-td">
                  <input class="bt-input"
                    :value="row.billing.monthly_fee_cosec"
                    @keydown="allowDigitsOnly"
                    @focus="clearZeroOnFocus(row.billing, 'monthly_fee_cosec', $event)"
                    @input="reformatOnInput($event, row.billing, 'monthly_fee_cosec')"
                    @blur="setZeroIfEmpty(row.billing, 'monthly_fee_cosec')"
                  />
                </td>
                <td class="bt-td">
                  <input class="bt-input"
                    :value="row.billing.monthly_fee_sst"
                    @keydown="allowDigitsOnly"
                    @focus="clearZeroOnFocus(row.billing, 'monthly_fee_sst', $event)"
                    @input="reformatOnInput($event, row.billing, 'monthly_fee_sst')"
                    @blur="setZeroIfEmpty(row.billing, 'monthly_fee_sst')"
                  />
                </td>
                <td class="bt-td">
                  <input class="bt-input"
                    :value="row.billing.monthly_fee_payroll"
                    @keydown="allowDigitsOnly"
                    @focus="clearZeroOnFocus(row.billing, 'monthly_fee_payroll', $event)"
                    @input="reformatOnInput($event, row.billing, 'monthly_fee_payroll')"
                    @blur="setZeroIfEmpty(row.billing, 'monthly_fee_payroll')"
                  />
                </td>
                <td class="bt-td bt-td-date" :class="{ 'bt-overdue': isOverdue(row) }">
                  <span v-if="isOverdue(row)" class="bt-overdue-tag">to bill</span>
                  <span>{{ formatNextBillingDate(row) }}</span>
                </td>
                <td class="bt-td bt-td-datepicker">
                  <billing-date-picker
                    :value="row.billing.last_invoice_date"
                    @input="onLastInvoiceDate(row.billing, $event)"
                  />
                </td>
                <td class="bt-td">
                  <input class="bt-input"
                    :value="row.billing.tax"
                    @keydown="allowDigitsOnly"
                    @focus="clearZeroOnFocus(row.billing, 'tax', $event)"
                    @input="reformatOnInput($event, row.billing, 'tax')"
                    @blur="setZeroIfEmpty(row.billing, 'tax')"
                  />
                </td>
                <td class="bt-td">
                  <input class="bt-input"
                    :value="row.billing.cosec"
                    @keydown="allowDigitsOnly"
                    @focus="clearZeroOnFocus(row.billing, 'cosec', $event)"
                    @input="reformatOnInput($event, row.billing, 'cosec')"
                    @blur="setZeroIfEmpty(row.billing, 'cosec')"
                  />
                </td>
                <td class="bt-td">
                  <input class="bt-input"
                    :value="row.billing.sst"
                    @keydown="allowDigitsOnly"
                    @focus="clearZeroOnFocus(row.billing, 'sst', $event)"
                    @input="reformatOnInput($event, row.billing, 'sst')"
                    @blur="setZeroIfEmpty(row.billing, 'sst')"
                  />
                </td>
                <td class="bt-td">
                  <input class="bt-input"
                    :value="row.billing.payroll_monthly"
                    @keydown="allowDigitsOnly"
                    @focus="clearZeroOnFocus(row.billing, 'payroll_monthly', $event)"
                    @input="reformatOnInput($event, row.billing, 'payroll_monthly')"
                    @blur="setZeroIfEmpty(row.billing, 'payroll_monthly')"
                  />
                </td>
                <td class="bt-td">
                  <input class="bt-input"
                    :value="row.billing.payroll_yearly"
                    @keydown="allowDigitsOnly"
                    @focus="clearZeroOnFocus(row.billing, 'payroll_yearly', $event)"
                    @input="reformatOnInput($event, row.billing, 'payroll_yearly')"
                    @blur="setZeroIfEmpty(row.billing, 'payroll_yearly')"
                  />
                </td>
                <td class="bt-td">
                  <input class="bt-input"
                    :value="row.billing.tp_reminder"
                    @keydown="allowDigitsOnly"
                    @focus="clearZeroOnFocus(row.billing, 'tp_reminder', $event)"
                    @input="reformatOnInput($event, row.billing, 'tp_reminder')"
                    @blur="setZeroIfEmpty(row.billing, 'tp_reminder')"
                  />
                </td>
                <td class="bt-td bt-td-total">{{ rowTotal(row) }}</td>
                <td class="bt-td">
                  <input class="bt-input bt-input--wide" v-model="row.billing.commission_to" placeholder="Commission to…" />
                </td>
                <td class="bt-td">
                  <input class="bt-input bt-input--wide" v-model="row.billing.commission_from" placeholder="Commission from…" />
                </td>
                <td class="bt-td bt-td-actions">
                  <div class="ap-row-actions">
                    <button class="ap-row-action" title="View" @click="viewingClient = row; showViewModal = true">
                      <fa-icon :icon="['fas', 'eye']" />
                    </button>
                    <button class="ap-row-action" title="Edit" @click="$router.push({ name: 'Admin-Edit-Client', params: { lang: $route.params.lang, id: row.id } })">
                      <fa-icon :icon="['fas', 'edit']" />
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div v-if="!loading && totalPages > 1" class="ap-table-foot">
          <div class="ap-table-info">
            Showing <b>{{ pageOffset + 1 }}</b>–<b>{{ Math.min(pageOffset + pageSize, filteredRows.length) }}</b> of <b>{{ filteredRows.length }}</b>
          </div>
          <div class="ap-pager">
            <button :disabled="currentPage <= 1" @click="currentPage--">
              <fa-icon :icon="['fas', 'chevron-left']" />
            </button>
            <button
              v-for="p in visiblePages"
              :key="p"
              :class="{ active: p === currentPage }"
              :disabled="p === '…'"
              @click="typeof p === 'number' && (currentPage = p)"
            >{{ p }}</button>
            <button :disabled="currentPage >= totalPages" @click="currentPage++">
              <fa-icon :icon="['fas', 'chevron-right']" />
            </button>
          </div>
        </div>

      </div>
    </main>
  </div>
</template>

<script>
import adminSessionMixin from '@/mixins/adminSessionMixin'
import AdminHeader from '@/components/admin/AdminHeader.vue'
import ConfirmModal from '@/components/shared/ConfirmModal.vue'
import SessionModal from '@/components/shared/SessionModal.vue'
import AlertModal from '@/components/shared/AlertModal.vue'
import BillingDatePicker from '@/components/form/BillingDatePicker.vue'
import ClientDetailsModal from '@/components/shared/ClientDetailsModal.vue'

const NUMERIC_FIELDS = [
  'monthly_fee_cosec', 'monthly_fee_sst', 'monthly_fee_payroll',
  'tax', 'cosec', 'sst', 'payroll_monthly', 'payroll_yearly', 'tp_reminder'
]

export default {
  name: 'admin-task-billing',
  mixins: [adminSessionMixin],
  components: { AdminHeader, ConfirmModal, SessionModal, AlertModal, BillingDatePicker, ClientDetailsModal },

  data () {
    return {
      billingRows: [],
      serverDate: null,
      loading: true,
      saving: false,
      searchQuery: '',
      statusFilter: 'active',
      statusOptions: [
        { value: '', label: 'All' },
        { value: 'active', label: 'Active' },
        { value: 'pending', label: 'Pending' },
        { value: 'dormant', label: 'Dormant' }
      ],
      currentPage: 1,
      pageSize: 20,
      showPopup: false,
      popupMessage: '',
      popupStatus: 'success',
      tableHeight: 'auto',
      viewingClient: null,
      showViewModal: false
    }
  },

  computed: {
    filteredRows () {
      const q = this.searchQuery.toLowerCase().trim()
      return this.billingRows.filter(r => {
        if (this.statusFilter && (r.status || 'active').toLowerCase() !== this.statusFilter) return false
        if (q && !(r.client || '').toLowerCase().includes(q) && !(r.initial || '').toLowerCase().includes(q)) return false
        return true
      })
    },
    totalPages () {
      return Math.max(1, Math.ceil(this.filteredRows.length / this.pageSize))
    },
    paginatedRows () {
      const start = (this.currentPage - 1) * this.pageSize
      return this.filteredRows.slice(start, start + this.pageSize)
    },
    pageOffset () {
      return (this.currentPage - 1) * this.pageSize
    },
    visiblePages () {
      const total = this.totalPages
      const cur = this.currentPage
      if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1)
      const pages = [1]
      if (cur > 3) pages.push('…')
      for (let i = Math.max(2, cur - 1); i <= Math.min(total - 1, cur + 1); i++) pages.push(i)
      if (cur < total - 2) pages.push('…')
      pages.push(total)
      return pages
    }
  },

  watch: {
    searchQuery () { this.currentPage = 1; this.$nextTick(this.computeTableHeight) },
    statusFilter () { this.currentPage = 1; this.$nextTick(this.computeTableHeight) }
  },

  async mounted () {
    if (localStorage.getItem('adminRole') !== 'super_admin') {
      this.$router.replace({ name: 'Admin-Panel', params: { lang: this.$route.params.lang } })
      return
    }
    this.startSessionTimer()
    await this.loadData()
    window.addEventListener('resize', this.computeTableHeight)
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.computeTableHeight)
  },

  methods: {
    computeTableHeight () {
      if (!this.$refs.calWrap) return
      this.tableHeight = 'auto'
      this.$nextTick(() => {
        const wrap = this.$refs.calWrap
        if (!wrap) return
        const h = wrap.scrollHeight
        this.tableHeight = `${h < 600 ? h + 15 : 600}px`
      })
    },
    async loadData () {
      this.loading = true
      try {
        const data = await this.adminFetch('/api/admin/tasks/billing-clients.php')
        if (data && data.success) {
          this.serverDate = data.server_date
          this.billingRows = data.clients.map(client => {
            NUMERIC_FIELDS.forEach(field => {
              client.billing[field] = this.toMoneyFormat(client.billing[field])
            })
            return client
          })
          this.$nextTick(this.computeTableHeight)
        }
      } catch (e) {
        console.error('Billing load error:', e)
      } finally {
        this.loading = false
      }
    },

    toMoneyFormat (raw) {
      const digits = String(raw || '0').replace(/\D/g, '')
      const num = parseInt(digits, 10)
      return (!digits || isNaN(num) || num === 0) ? '0' : num.toLocaleString('en-MY')
    },

    clearZeroOnFocus (billing, field, event) {
      if (billing[field] === '0') {
        billing[field] = ''
        event.target.value = ''
      }
    },

    reformatOnInput (event, billing, field) {
      const input = event.target
      const rawVal = input.value
      const cursorPos = input.selectionStart

      // Count digits before cursor so we can restore position after reformat
      const digitsBeforeCursor = rawVal.slice(0, cursorPos).replace(/\D/g, '').length

      // Strip non-digits and reformat
      const digitsOnly = rawVal.replace(/\D/g, '')
      const num = parseInt(digitsOnly, 10)
      const formatted = digitsOnly === '' ? '' : (isNaN(num) ? '' : num.toLocaleString('en-MY'))

      billing[field] = formatted

      // Restore cursor after Vue re-renders the :value binding
      this.$nextTick(() => {
        let digitsSeen = 0
        let newPos = formatted.length
        for (let i = 0; i < formatted.length; i++) {
          if (/\d/.test(formatted[i])) {
            digitsSeen++
            if (digitsSeen === digitsBeforeCursor) {
              newPos = i + 1
              break
            }
          }
        }
        if (digitsBeforeCursor === 0) newPos = 0
        input.setSelectionRange(newPos, newPos)
      })
    },

    setZeroIfEmpty (billing, field) {
      if (!billing[field] || billing[field].trim() === '') {
        billing[field] = '0'
      }
    },

    onLastInvoiceDate (billing, date) {
      this.$set(billing, 'last_invoice_date', date)
    },

    computeNextBillingDate (inceptionDateStr) {
      if (!inceptionDateStr || !this.serverDate) return null
      const parts = inceptionDateStr.split('-').map(Number)
      const incMo = parts[1]
      const incDay = parts[2]
      const serverDate = new Date(this.serverDate)
      const serverYear = serverDate.getFullYear()
      const candidate = new Date(serverYear, incMo - 1, incDay)
      if (candidate >= serverDate) {
        return new Date(serverYear, incMo - 2, incDay)
      }
      return new Date(serverYear - 1, incMo - 2, incDay)
    },

    allowDigitsOnly (event) {
      const allowed = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
        'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End']
      if (allowed.includes(event.key)) return
      if ((event.ctrlKey || event.metaKey) && ['a', 'c', 'v', 'x'].includes(event.key.toLowerCase())) return
      if (!/^\d$/.test(event.key)) event.preventDefault()
    },

    isOverdue (row) {
      const nbd = this.computeNextBillingDate(row.inception_date)
      if (!nbd) return false
      const lid = (row.billing.last_invoice_date || '').trim()
      if (!lid) return true
      const lastInvDate = new Date(lid)
      if (isNaN(lastInvDate.getTime())) return true
      // show "to bill" when last invoice date < next billing date
      return lastInvDate < nbd
    },

    formatNextBillingDate (row) {
      const d = this.computeNextBillingDate(row.inception_date)
      if (!d) return '—'
      const yyyy = d.getFullYear()
      const mm = String(d.getMonth() + 1).padStart(2, '0')
      const dd = String(d.getDate()).padStart(2, '0')
      return `${yyyy}-${mm}-${dd}`
    },

    formatIncDate (dateStr) {
      if (!dateStr) return '—'
      const parts = dateStr.split('-')
      if (parts.length !== 3) return dateStr
      return `${parts[2]}/${parts[1]}/${parts[0]}`
    },

    rowTotal (row) {
      const total = NUMERIC_FIELDS.reduce((sum, f) => {
        const v = parseFloat((row.billing[f] || '0').replace(/,/g, ''))
        return sum + (isNaN(v) ? 0 : v)
      }, 0)
      return total.toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },

    async saveAll () {
      this.saving = true
      try {
        const payload = this.billingRows.map(row => ({
          id: row.id,
          billing: { ...row.billing }
        }))
        const data = await this.adminFetch('/api/admin/tasks/update-billing.php', {
          method: 'POST',
          body: JSON.stringify({ clients: payload })
        })
        if (data && data.success) {
          this.openPopup('Billing table updated successfully.', 'success')
        } else {
          this.openPopup((data && data.message) || 'Failed to update billing.', 'error')
        }
      } catch (e) {
        this.openPopup('Failed to update billing.', 'error')
      } finally {
        this.saving = false
      }
    },

    openPopup (message, status = 'success') {
      this.popupMessage = message
      this.popupStatus = status
      this.showPopup = true
    },

    closePopup () {
      this.showPopup = false
      this.popupMessage = ''
    }
  }
}
</script>
